/**
 * Platform console hooks for the Stories review queue
 * (server/src/routes/adminStories.routes.ts — platform admins only; every
 * other caller gets a 404, so these are only ever mounted inside /admin).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "../../../lib/api/client";
import type { AdminStorySubmission, StorySubmissionStatus } from "@shared/types/stories";

export const adminStoriesKeys = {
  all: ["admin", "stories"] as const,
  list: (status: StorySubmissionStatus) => ["admin", "stories", status] as const,
};

export function useAdminStorySubmissions(status: StorySubmissionStatus) {
  return useQuery<AdminStorySubmission[], ApiError>({
    queryKey: adminStoriesKeys.list(status),
    queryFn: () =>
      apiClient<AdminStorySubmission[]>(
        `/api/admin/stories/submissions?status=${encodeURIComponent(status)}`,
      ),
  });
}

export type ReviewAction = "publish" | "reject";

/** Publish (optional note) or reject / take down (note required). */
export function useReviewStory() {
  const queryClient = useQueryClient();
  return useMutation<AdminStorySubmission, ApiError, { id: string; action: ReviewAction; note: string }>({
    mutationFn: ({ id, action, note }) =>
      apiClient<AdminStorySubmission>(
        `/api/admin/stories/submissions/${encodeURIComponent(id)}/${action}`,
        {
          method: "POST",
          body: JSON.stringify(note.trim() ? { note: note.trim() } : {}),
        },
      ),
    // A review moves the row between tabs: refetch every tab.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: adminStoriesKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["stories"] });
    },
  });
}
